import { useState, useEffect, useCallback } from 'react'
import { useRecoilState, useRecoilValue } from 'recoil'
import BigNumber from 'bignumber.js'
import TabNavigation from '@/components/TabNavigation'
import ConnectWalletView from '@/components/ConnectWalletView'
import WalletBalance from '@/components/wallet/WalletBalance'
import AssetBalance from '@/components/wallet/AssetBalance'
import WalletOpreationPopup from '@/components/wallet/WalletOpreationPopup'
import WalletUnbindPopup from '@/components/wallet/WalletUnbindPopup'
import IconArrowLeft from '@/components/icons/IconArrowLeft'
import { markPriceMapState } from '@/recoil/Market'
import {
  tokenListState,
  walletState,
  accountWalletState
} from '@/recoil/Wallet'
import { collateralInfoValue } from '@/recoil/Position'
import { useWalletSelector } from '@/contexts/WalletSelectorContext'
import fetcher from '@/api/fetcher'
import contractHelper from '@/api/contractHelper'
import { numericSort } from '@/utils/sorting'
import { USDC_TOKEN_LIST } from '@/constants/token'
import './Wallet.css'

function getMarkPrice(markPriceMap, symbol) {
  return USDC_TOKEN_LIST.includes(symbol)
    ? 1
    : markPriceMap[`SPOT_${symbol}_USDC.e`] || 0
}

export default function Wallet() {
  const { accountId } = useWalletSelector()
  const [markPriceMap] = useRecoilState(markPriceMapState)
  const [tokenList, setTokenList] = useRecoilState(tokenListState)
  const [wallet, setWallet] = useRecoilState(walletState)
  const [accountWallet] = useRecoilState(accountWalletState)
  const { freeCollateral } = useRecoilValue(collateralInfoValue)
  const [selectedAssetList, setSelectedAssetList] = useState(['NEAR'])
  const [showAssetDetailInfo, setShowAssetDetailInfo] = useState(false)
  const [showWalletOperationPopup, setShowWalletOperationPopup] =
    useState(false)
  const [showWalletUnbindPopup, setShowWalletUnbindPopup] = useState(false)
  const [isDeposit, setIsDeposit] = useState(false)

  const updateWalletBalance = useCallback(
    () =>
      fetcher
        .getTokenList()
        .then(tokenList =>
          Promise.all(contractHelper.getWalletBalance(tokenList))
        )
        .then(walletBalanceList => {
          const list = []
          const map = {}
          walletBalanceList.forEach(({ token, amount, decimals }) => {
            token !== 'WOO' && list.push(token)
            map[token] = {
              token,
              decimals,
              balance: amount
            }
          })
          setWallet(map)
          setTokenList(list)
        }),
    [setWallet, setTokenList]
  )

  const toggleShowAssetDetail = useCallback(
    () => setShowAssetDetailInfo(!showAssetDetailInfo),
    [showAssetDetailInfo]
  )

  const onTransferButtonClick = useCallback((token, isDeposit) => {
    setSelectedAssetList([token])
    setIsDeposit(isDeposit)
    setShowWalletOperationPopup(true)
  }, [])

  useEffect(() => {
    if (!accountId) {
      return
    }

    updateWalletBalance()
  }, [accountId, updateWalletBalance])

  let totalBalance = BigNumber(0)
  let walletTotalBalance = BigNumber(0)
  const isReady = tokenList.length > 0 && Object.keys(accountWallet).length > 0
  const sortedAssetList =
    accountId && isReady
      ? tokenList.concat([]).sort((a, b) => {
          const priceA = getMarkPrice(markPriceMap, a)
          const priceB = getMarkPrice(markPriceMap, b)
          const holdingA = accountWallet[a]?.holding || 0
          const holdingB = accountWallet[b]?.holding || 0
          return numericSort(
            BigNumber(holdingA).times(priceA).toNumber(),
            BigNumber(holdingB).times(priceB).toNumber(),
            false
          )
        })
      : []
  const AssetList = sortedAssetList.map(token => {
    const { holding = 0, available = 0 } = accountWallet[token] || {}
    const isUsdc = USDC_TOKEN_LIST.includes(token)
    const price = getMarkPrice(markPriceMap, token)
    const accountWalletBalanceInUsdc = BigNumber(holding)
      .times(price)
      .toNumber()
    const walletBalance = BigNumber(wallet[token]?.balance)
      .times(price)
      .toNumber()

    totalBalance = totalBalance.plus(accountWalletBalanceInUsdc)
    walletTotalBalance = walletTotalBalance.plus(walletBalance)

    return (
      <div key={token} className="asset-list-row">
        <AssetBalance
          symbol={token}
          balance={holding}
          available={isUsdc ? freeCollateral : available}
          valueInUsdc={accountWalletBalanceInUsdc}
          showAssetDetailInfo={showAssetDetailInfo}
          toggleShowAssetDetail={toggleShowAssetDetail}
        />
        <div className="transfer-button-container">
          <div
            className="tooltip-container transfer-button"
            onClick={() => onTransferButtonClick(token, true)}
          >
            <IconArrowLeft className="icon-arrow-left" />
            <div className="tooltip">Deposit into Trading Wallet</div>
          </div>
          <div
            className="tooltip-container transfer-button reverse"
            onClick={() => onTransferButtonClick(token, false)}
          >
            <IconArrowLeft className="icon-arrow-left" />
            <div className="tooltip">Withdraw to DeFi Wallet</div>
          </div>
        </div>
        <AssetBalance
          symbol={token}
          balance={wallet[token]?.balance}
          valueInUsdc={walletBalance}
          isDeFiWallet={true}
        />
      </div>
    )
  })

  return (
    <>
      <div className="wallet-container">
        <TabNavigation />
        <div className="wallet card noselect">
          {accountId ? (
            <div className="wallet-total-balance-container">
              <WalletBalance
                isLoading={!isReady}
                label="Trading Wallet"
                balance={totalBalance.toNumber()}
                currency="USDC"
              />
              <WalletBalance
                isDeFiWallet={true}
                isLoading={!isReady}
                label="DeFi Wallet"
                balance={walletTotalBalance.toNumber()}
                currency="USDC"
                actionCallback={() => setShowWalletUnbindPopup(true)}
              />
            </div>
          ) : (
            <ConnectWalletView />
          )}
          {AssetList}
        </div>
      </div>
      {isReady && (
        <WalletOpreationPopup
          show={showWalletOperationPopup}
          accountWallet={accountWallet}
          wallet={wallet}
          isDeposit={isDeposit}
          assetList={sortedAssetList}
          selectedAssetList={selectedAssetList}
          setSelectedAssetList={setSelectedAssetList}
          updateWalletBalance={updateWalletBalance}
          hidePopupCallback={() => setShowWalletOperationPopup(false)}
        />
      )}
      {accountId && (
        <WalletUnbindPopup
          show={showWalletUnbindPopup}
          hidePopupCallback={() => setShowWalletUnbindPopup(false)}
        />
      )}
    </>
  )
}
